import { randUuid } from '@ngneat/falso'
import { attach, createEffect, createEvent, createStore } from 'effector'

import { toast } from '@/models/toasts.ts'
import { addMediaFx } from '@/models/vault/medias'
import type { UploadFileItem } from '@/modules/vault/types.ts'
import { handleError } from '@/utils/error'

export const resetFilesToAdd = createEvent('reset files to add')
export const setFilesToAdd = createEvent('set files to add')
export const addFiles = createEvent<File[]>('add files to add')
export const removeFile = createEvent<number>('remove file from add')
export const updateFileToAdd = createEvent<UploadFileItem>('update file to add')
export const $filesToAdd = createStore<UploadFileItem[]>([], {
  name: 'files to add',
})
  .on(setFilesToAdd, (_, files) => files)
  .on(addFiles, (files, data) =>
    files.concat(
      data.map((file) => ({
        file,
        id: randUuid(),
        uploadingProgress: 0,
        isUploading: false,
      })),
    ),
  )
  .on(removeFile, (files, index) => files.filter((_, i) => i !== index))
  .on(updateFileToAdd, (files, data) =>
    files.map((file) => (file.id === data.id ? data : file)),
  )
  .reset(resetFilesToAdd)

export const uploadAllMediaFx = attach({
  name: 'upload all media',
  source: {
    files: $filesToAdd,
  },
  mapParams: (selectedCategoryId: number | undefined, { files }) => ({
    files,
    selectedCategoryId,
  }),
  effect: createEffect(
    async ({
      files,
      selectedCategoryId,
    }: {
      files: UploadFileItem[]
      selectedCategoryId?: number
    }) => {
      const results = await Promise.allSettled(
        files.map((f) => addMediaFx({ selectedCategoryId, ...f })),
      )

      const hasAnySuccess = results.some(({ status }) => status === 'fulfilled')

      if (!hasAnySuccess && results.length > 1) {
        throw new Error()
      }

      return results
    },
  ),
})

uploadAllMediaFx.failData.watch((error) => {
  handleError(
    error,
    'Unexpected error occurred while uploading files, please try again later',
  )
})

uploadAllMediaFx.done.watch(() => {
  toast.success('Processing media... Click refresh to update the list')
})
